import { Badge, Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { TppCardSummary } from '../../../services/transitPassPool.service';
import { tppActivateKeyHandler } from './tppUiShared';
import { useTppMdUp } from './useTppMdUp';
import {
  isTppBorrowable,
  isTppChargeActionVisible,
  isTppNeedsChargeFirst,
} from './tppCardPredicates';

type TppCardPoolListProps = {
  items: TppCardSummary[];
  selectedId: number | null | undefined;
  /** 1枚プール: 表ヘッダを出さず1行の要約＋操作にする */
  soloLayout?: boolean;
  /** soloLayout 時に概況カードを出さないためのリロード */
  onReload?: () => void;
  money: (n: number) => string;
  onSelect: (card: TppCardSummary) => void;
  onBorrow: (card: TppCardSummary) => void;
  onReturn: (card: TppCardSummary) => void;
  onCharge: (card: TppCardSummary) => void;
  onEdit: (card: TppCardSummary) => void;
};

function TppCardStatusBadges({ card }: { card: TppCardSummary }) {
  const { t } = useTranslation();
  const badges: Array<{ bg: string; label: string }> = [];
  if (card.isCheckedOut) {
    badges.push({ bg: 'warning', label: t('tpp.status_out') });
    if (!card.isActive) {
      badges.push({ bg: 'secondary', label: t('tpp.card_inactive') });
    }
  } else if (!card.isActive) {
    badges.push({ bg: 'secondary', label: t('tpp.card_inactive') });
  } else if (isTppNeedsChargeFirst(card)) {
    badges.push({ bg: 'info', label: t('tpp.status_needs_charge') });
  } else {
    badges.push({ bg: 'success', label: t('tpp.status_available') });
  }
  return (
    <div className="d-flex flex-wrap gap-1 align-items-center justify-content-end justify-content-md-start">
      {badges.map((b) => (
        <Badge key={b.label} bg={b.bg}>
          {b.label}
        </Badge>
      ))}
    </div>
  );
}

function TppCardPrimaryActions({
  card,
  onBorrow,
  onReturn,
  onCharge,
  onEdit,
  compact = false,
}: {
  card: TppCardSummary;
  onBorrow: (card: TppCardSummary) => void;
  onReturn: (card: TppCardSummary) => void;
  onCharge: (card: TppCardSummary) => void;
  onEdit: (card: TppCardSummary) => void;
  /** モバイルカード: 主操作を幅いっぱいに */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const canBorrow = isTppBorrowable(card);
  const needsChargeFirst = isTppNeedsChargeFirst(card);
  const showCharge = isTppChargeActionVisible(card);
  const primaryClass = compact ? 'flex-grow-1' : undefined;
  return (
    <div className="tpp-card-actions">
      {!card.isCheckedOut ? (
        canBorrow ? (
          <Button size="sm" variant="primary" className={primaryClass} onClick={() => onBorrow(card)}>
            {t('tpp.borrow')}
          </Button>
        ) : null
      ) : (
        <Button size="sm" variant="warning" className={primaryClass} onClick={() => onReturn(card)}>
          {t('tpp.return')}
        </Button>
      )}
      {showCharge ? (
        <Button
          size="sm"
          variant={needsChargeFirst ? 'primary' : 'outline-success'}
          className={compact && needsChargeFirst ? 'flex-grow-1' : undefined}
          title={
            needsChargeFirst
              ? t('tpp.borrow_disabled_no_balance')
              : card.isCheckedOut
                ? t('tpp.charge_while_checked_out_title')
                : undefined
          }
          onClick={() => onCharge(card)}
        >
          {t('tpp.charge')}
        </Button>
      ) : null}
      <Button
        size="sm"
        variant="outline-secondary"
        aria-label={t('tpp.edit_card_aria', { n: card.displayNumber })}
        title={t('common.edit')}
        onClick={() => onEdit(card)}
      >
        <i className="fas fa-pen" aria-hidden />
      </Button>
    </div>
  );
}

function TppPhysicalIdLabel({ physicalId }: { physicalId: string }) {
  const { t } = useTranslation();
  const id = physicalId.trim();
  if (!id) {
    return <span className="text-muted fst-italic">{t('common.not_set')}</span>;
  }
  return <>{id}</>;
}

/** カード一覧: 狭幅はカード型、md以上は表（操作ボタンは共通化） */
export default function TppCardPoolList({
  items,
  selectedId,
  soloLayout = false,
  onReload,
  money,
  onSelect,
  onBorrow,
  onReturn,
  onCharge,
  onEdit,
}: TppCardPoolListProps) {
  const { t } = useTranslation();
  const mdUp = useTppMdUp();

  if (soloLayout && items.length === 1) {
    const c = items[0];
    return (
      <div className="px-3 py-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div className="d-flex flex-wrap align-items-center gap-2 min-w-0">
          <span className="fw-semibold text-nowrap">
            {t('tpp.card_heading', { n: c.displayNumber })}
          </span>
          <span className="small text-muted text-break">
            <TppPhysicalIdLabel physicalId={c.physicalId} />
          </span>
          <TppCardStatusBadges card={c} />
          <strong
            className={`text-nowrap tabular-nums ${c.balanceNumber < 0 ? 'text-danger' : ''}`}
          >
            {money(c.balanceNumber)}
          </strong>
          {onReload ? (
            <Button
              size="sm"
              variant="link"
              className="text-decoration-none px-1 py-0"
              onClick={onReload}
              aria-label={t('common.reload')}
              title={t('common.reload')}
            >
              <i className="fas fa-sync-alt" aria-hidden />
            </Button>
          ) : null}
        </div>
        <div className="d-inline-flex flex-wrap gap-1">
          <TppCardPrimaryActions
            card={c}
            onBorrow={onBorrow}
            onReturn={onReturn}
            onCharge={onCharge}
            onEdit={onEdit}
          />
        </div>
      </div>
    );
  }

  if (!mdUp) {
    return (
      <div className="p-2">
        <div className="d-flex flex-column gap-2">
          {items.map((c) => (
            <div
              key={c.id}
              className={`rounded border p-3 ${
                selectedId === c.id ? 'border-primary bg-primary-subtle' : 'bg-white'
              }`}
              aria-current={selectedId === c.id ? 'true' : undefined}
            >
              <div
                role="button"
                tabIndex={0}
                className="d-flex justify-content-between align-items-start gap-2 mb-2"
                style={{ cursor: 'pointer' }}
                aria-label={t('tpp.select_card_aria', { n: c.displayNumber })}
                onClick={() => onSelect(c)}
                onKeyDown={tppActivateKeyHandler(() => onSelect(c))}
              >
                <div>
                  <div className="fw-semibold">{t('tpp.card_heading', { n: c.displayNumber })}</div>
                  <div className="small text-muted text-break">
                    <TppPhysicalIdLabel physicalId={c.physicalId} />
                  </div>
                </div>
                <div className="text-end">
                  <div className="mb-1">
                    <TppCardStatusBadges card={c} />
                  </div>
                  <div
                    className={`fw-semibold text-nowrap ${c.balanceNumber < 0 ? 'text-danger' : ''}`}
                  >
                    {money(c.balanceNumber)}
                  </div>
                </div>
              </div>
              <div className="d-flex flex-wrap gap-2 align-items-stretch">
                <TppCardPrimaryActions
                  card={c}
                  compact
                  onBorrow={onBorrow}
                  onReturn={onReturn}
                  onCharge={onCharge}
                  onEdit={onEdit}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <Table hover striped className="mb-0 align-middle">
        <thead>
          <tr>
            <th scope="col">{t('tpp.card_no')}</th>
            <th scope="col">{t('tpp.table_status')}</th>
            <th scope="col" className="text-end">
              {t('tpp.balance')}
            </th>
            <th scope="col" className="text-center small" style={{ minWidth: '11rem' }}>
              {t('tpp.table_actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => {
            const selected = selectedId === c.id;
            return (
              <tr
                key={c.id}
                className={selected ? 'table-primary' : undefined}
                style={{ cursor: 'pointer' }}
                tabIndex={0}
                aria-current={selected ? 'true' : undefined}
                aria-label={t('tpp.select_card_aria', { n: c.displayNumber })}
                onClick={() => onSelect(c)}
                onKeyDown={tppActivateKeyHandler(() => onSelect(c))}
              >
                <td className="fw-semibold">
                  <div>{c.displayNumber}</div>
                  <div
                    className="small text-muted text-break fw-normal"
                    style={{ maxWidth: 'min(28rem, 40vw)' }}
                  >
                    <TppPhysicalIdLabel physicalId={c.physicalId} />
                  </div>
                </td>
                <td>
                  <TppCardStatusBadges card={c} />
                </td>
                <td className="text-nowrap text-end tabular-nums">
                  <strong className={c.balanceNumber < 0 ? 'text-danger' : undefined}>
                    {money(c.balanceNumber)}
                  </strong>
                </td>
                <td
                  className="text-center text-nowrap align-middle"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="d-inline-flex flex-wrap gap-1 justify-content-center">
                    <TppCardPrimaryActions
                      card={c}
                      onBorrow={onBorrow}
                      onReturn={onReturn}
                      onCharge={onCharge}
                      onEdit={onEdit}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}
